/**
 * RebalanceQueuePage — Human-in-loop trade approval queue
 *
 * Shows pending trades from the Alpha Engine auto-pot,
 * allows approve / reject / execute, and displays history.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  ShieldCheck, XCircle, Play, Clock, CheckCircle,
  RefreshCw, AlertTriangle, TrendingUp, TrendingDown,
  Zap, HelpCircle, Info, type LucideIcon,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api, formatExecutionResult, normalizeApiErrorDetail } from '../lib/api'
import PageHeader from '@/components/ui/PageHeader'

function InfoTip({ text }: { text: string }) {
  return (
    <span className="inline-flex items-center cursor-help" title={text}>
      <HelpCircle size={11} className="text-text-muted opacity-50 hover:opacity-100 transition" />
    </span>
  )
}

function HowQueueWorks() {
  const [open, setOpen] = useState(false)
  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-surface-hover/40 transition"
      >
        <div className="flex items-center gap-2">
          <Info size={15} className="text-blue-400" />
          <span className="text-sm font-semibold text-text-primary">How the approval queue works</span>
          <span className="text-[10px] text-text-muted">— click for detail</span>
        </div>
        <span className={clsx('text-text-muted text-xs transition', open && 'rotate-90')}>▸</span>
      </button>
      {open && (
        <div className="border-t border-border px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-5 text-xs">
          <div>
            <div className="text-[10px] uppercase text-text-muted mb-1.5 font-semibold tracking-wider">
              Where trades come from
            </div>
            <p className="text-text-secondary leading-relaxed">
              Every 2 hours the <span className="text-text-primary">Alpha Engine</span> scans signals, scores
              opportunities, and enqueues anything hitting <span className="text-text-primary">50+ conviction</span> here
              for your approval. Stop-loss SELLs for positions at −10% are enqueued the same way.
            </p>
            <p className="text-text-secondary mt-2">
              Source of truth: the <code className="text-text-secondary">rebalance_queue</code> table in Postgres —
              queued and approved trades survive restarts and deploys. Executed Alpha Engine SELLs also update{' '}
              <code className="text-text-secondary">alpha_engine_positions</code>.
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase text-text-muted mb-1.5 font-semibold tracking-wider">
              Status lifecycle
            </div>
            <ul className="text-text-secondary space-y-1.5">
              <li><span className="text-amber-300 font-medium">pending</span> — awaiting human review</li>
              <li><span className="text-blue-300 font-medium">approved</span> — you've greenlit it; waiting for Execute to send to broker</li>
              <li><span className="text-emerald-300 font-medium">executed</span> — sent to Alpaca (paper by default); order ID returned</li>
              <li><span className="text-rose-300 font-medium">rejected / failed</span> — you declined it, or Alpaca returned an error</li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase text-text-muted mb-1.5 font-semibold tracking-wider">
              Safety rails
            </div>
            <p className="text-text-secondary leading-relaxed">
              Only <span className="text-text-primary">discretionary</span> accounts can be touched; manager-controlled
              wrappers are blocked server-side. Each trade is re-checked at execute time against the per-trade
              value limit. For Alpha Engine SELLs, executing here also closes the matching
              <code className="text-text-secondary"> alpha_engine_positions</code> row so realised P&L flows
              into NAV on the next snapshot.
            </p>
            <p className="text-text-secondary mt-2">
              See the full flow at <a href="/app/system-map" className="text-blue-400 hover:underline">/app/system-map</a>.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Types ────────────────────────────────────────────────────────────────────

interface QueueTrade {
  id: number
  ticker: string
  action: 'BUY' | 'SELL'
  quantity: number
  reason: string
  estimated_value_gbp: number
  status: string
  created_at: string
}

interface HistoryTrade {
  id: number
  ticker: string
  action: 'BUY' | 'SELL'
  quantity: number
  status: string
  executed_at: string
  // Backend TradeOut.execution_result is Optional[dict] (Alpaca payload or {error})
  execution_result?: { order_id?: string | null; error?: string | null; [k: string]: unknown } | null
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function gbp(n: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n)
}

function relTime(ts?: string): string {
  if (!ts) return '-'
  try {
    const d = new Date(ts)
    if (isNaN(d.getTime())) return ts
    const diff = Date.now() - d.getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  } catch {
    return ts || '-'
  }
}

function fmtDate(ts?: string): string {
  if (!ts) return '-'
  try {
    return new Date(ts).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return ts
  }
}

function statusBadge(status: string) {
  const s = status.toLowerCase()
  if (s === 'approved')  return 'bg-blue-500/15 text-blue-400 border-blue-500/30'
  if (s === 'executed')  return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
  if (s === 'rejected')  return 'bg-rose-500/15 text-rose-400 border-rose-500/30'
  if (s === 'pending')   return 'bg-amber-500/15 text-amber-400 border-amber-500/30'
  return 'bg-surface-hover text-text-secondary border-border'
}

// ── Component ────────────────────────────────────────────────────────────────

export default function RebalanceQueuePage() {
  const [pending, setPending] = useState<QueueTrade[]>([])
  const [history, setHistory] = useState<HistoryTrade[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<Record<number, string>>({})

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [qRes, hRes] = await Promise.all([
        api.get('/api/rebalance-queue/queue'),
        api.get('/api/rebalance-queue/history'),
      ])
      // Both endpoints return BARE arrays (response_model=list[TradeOut]),
      // not {trades: [...]} — guard with Array.isArray for safety.
      setPending(Array.isArray(qRes.data) ? (qRes.data as QueueTrade[]) : [])
      setHistory(Array.isArray(hRes.data) ? (hRes.data as HistoryTrade[]) : [])
    } catch (err: any) {
      setError(normalizeApiErrorDetail(err?.response?.data?.detail, err?.message || 'Failed to load queue'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const doAction = async (id: number, action: 'approve' | 'reject' | 'execute') => {
    setActionLoading(prev => ({ ...prev, [id]: action }))
    try {
      await api.post(`/api/rebalance-queue/${action}/${id}`)
      await fetchAll()
    } catch (err: any) {
      setError(normalizeApiErrorDetail(err?.response?.data?.detail, `Failed to ${action} trade`))
    } finally {
      setActionLoading(prev => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    }
  }

  const pendingTrades  = pending.filter(t => t.status?.toLowerCase() === 'pending')
  const approvedTrades = pending.filter(t => t.status?.toLowerCase() === 'approved')

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto animate-fade-in">
      {/* Header */}
      <PageHeader
        title="Trade Approval Queue"
        icon={ShieldCheck}
        iconClassName="text-amber-400"
        subtitle="Alpha Engine queues trades here · you approve / reject · Execute sends to Alpaca."
        actions={
          <button
            type="button"
            onClick={fetchAll}
            disabled={loading}
            className="btn-secondary text-sm gap-2"
          >
            <RefreshCw size={14} className={clsx(loading && 'animate-spin')} />
            Refresh
          </button>
        }
      />

      <HowQueueWorks />

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm text-rose-400">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !pending.length && !history.length && (
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="skeleton h-40" />
          ))}
        </div>
      )}

      {/* ── Pending Trades ──────────────────────────────────────────────────── */}
      <section>
        <h2 className="panel-title mb-3 flex items-center gap-2">
          <Clock size={18} className="text-amber-400" />
          Pending Trades
          {pendingTrades.length > 0 && (
            <span className="badge-amber">
              {pendingTrades.length}
            </span>
          )}
        </h2>

        {!loading && pendingTrades.length === 0 && (
          <div className="card text-sm text-text-muted px-4 py-8 text-center">
            No pending trades in the queue
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 stagger-children">
          {pendingTrades.map(trade => (
            <TradeCard
              key={trade.id}
              trade={trade}
              actionLoading={actionLoading[trade.id]}
              onApprove={() => doAction(trade.id, 'approve')}
              onReject={() => doAction(trade.id, 'reject')}
            />
          ))}
        </div>
      </section>

      {/* ── Approved (Awaiting Execution) ───────────────────────────────────── */}
      <section>
        <h2 className="panel-title mb-3 flex items-center gap-2">
          <CheckCircle size={18} className="text-blue-400" />
          Approved &mdash; Awaiting Execution
          {approvedTrades.length > 0 && (
            <span className="badge-blue">
              {approvedTrades.length}
            </span>
          )}
        </h2>

        {!loading && approvedTrades.length === 0 && (
          <div className="card text-sm text-text-muted px-4 py-6 text-center">
            No approved trades awaiting execution
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {approvedTrades.map(trade => (
            <TradeCard
              key={trade.id}
              trade={trade}
              actionLoading={actionLoading[trade.id]}
              showExecute
              onExecute={() => doAction(trade.id, 'execute')}
            />
          ))}
        </div>
      </section>

      {/* ── History Table ───────────────────────────────────────────────────── */}
      <section>
        <h2 className="panel-title mb-3 flex items-center gap-2">
          <Zap size={18} className="text-text-muted" />
          Trade History
        </h2>

        <div className="card overflow-hidden">
          <div className="table-responsive overflow-x-auto">
            <table className="table-pro w-full text-sm">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Action</th>
                  <th className="!text-right">Qty</th>
                  <th>Status</th>
                  <th>Executed</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-text-muted">
                      No trade history yet
                    </td>
                  </tr>
                )}
                {history.map(t => (
                  <tr key={t.id}>
                    <td className="font-mono font-semibold text-text-primary">{t.ticker}</td>
                    <td>
                      <span className={clsx(
                        t.action === 'BUY' ? 'badge-green' : 'badge-red',
                      )}>
                        {t.action}
                      </span>
                    </td>
                    <td className="text-right font-mono text-text-secondary">{t.quantity}</td>
                    <td>
                      <span className={clsx(
                        'text-xs font-semibold px-2 py-0.5 rounded border',
                        statusBadge(t.status),
                      )}>
                        {t.status}
                      </span>
                    </td>
                    <td className="text-text-muted">{fmtDate(t.executed_at)}</td>
                    <td
                      className="text-text-secondary text-xs max-w-[200px] truncate"
                      title={formatExecutionResult(t.execution_result)}
                    >
                      {formatExecutionResult(t.execution_result)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  )
}

// ── Trade Card ───────────────────────────────────────────────────────────────

function TradeCard({
  trade,
  actionLoading,
  onApprove,
  onReject,
  onExecute,
  showExecute,
}: {
  trade: QueueTrade
  actionLoading?: string
  onApprove?: () => void
  onReject?: () => void
  onExecute?: () => void
  showExecute?: boolean
}) {
  const isBuy = trade.action === 'BUY'

  return (
    <div className="card card-hover p-5 flex flex-col gap-3">
      {/* Top row: ticker + action */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold font-mono text-text-primary">{trade.ticker}</span>
          <span className={clsx(
            'text-xs font-bold px-2.5 py-1 rounded-md flex items-center gap-1',
            isBuy
              ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
              : 'bg-rose-500/15 text-rose-400 border border-rose-500/30',
          )}>
            {isBuy ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {trade.action}
          </span>
        </div>
        <span className="text-xs text-text-muted">{relTime(trade.created_at)}</span>
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-text-muted">Quantity</span>
          <p className="font-mono font-semibold text-text-primary">{trade.quantity}</p>
        </div>
        <div>
          <span className="text-text-muted">Est. Value</span>
          <p className="font-mono font-semibold text-text-primary">{gbp(trade.estimated_value_gbp)}</p>
        </div>
      </div>

      {/* Reason */}
      {trade.reason && (
        <p className="text-xs text-text-secondary leading-relaxed bg-surface-DEFAULT rounded-lg px-3 py-2">
          {trade.reason}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-1">
        {trade.status?.toLowerCase() === 'pending' && (
          <>
            <button
              type="button"
              onClick={onApprove}
              disabled={!!actionLoading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg
                         bg-emerald-500/15 text-emerald-400 border border-emerald-500/30
                         hover:bg-emerald-500/25 transition-colors text-sm font-semibold
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle size={14} />
              {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
            </button>
            <button
              type="button"
              onClick={onReject}
              disabled={!!actionLoading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg
                         bg-rose-500/15 text-rose-400 border border-rose-500/30
                         hover:bg-rose-500/25 transition-colors text-sm font-semibold
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle size={14} />
              {actionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
            </button>
          </>
        )}
        {showExecute && trade.status?.toLowerCase() === 'approved' && (
          <button
            type="button"
            onClick={onExecute}
            disabled={!!actionLoading}
            className="btn-primary flex-1 text-sm font-bold gap-2"
          >
            <Play size={14} />
            {actionLoading === 'execute' ? 'Executing...' : 'Execute Trade'}
          </button>
        )}
      </div>
    </div>
  )
}
